import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// Tipo tema applicazione
export type AppTheme = 'light' | 'dark' | 'system'

// Filtri attivi per le varie sezioni
interface ActiveFilters {
  clients: Record<string, string>
  tasks: Record<string, string>
  projects: Record<string, string>
}

// Interfaccia stato UI
interface UIState {
  sidebarCollapsed: boolean
  sidebarMobileOpen: boolean
  theme: AppTheme
  activeFilters: ActiveFilters

  // Actions sidebar
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleMobileSidebar: () => void
  setMobileSidebarOpen: (open: boolean) => void

  // Actions tema
  setTheme: (theme: AppTheme) => void

  // Actions filtri
  setFilter: (section: keyof ActiveFilters, key: string, value: string) => void
  clearFilters: (section: keyof ActiveFilters) => void
  clearAllFilters: () => void
}

const defaultFilters: ActiveFilters = {
  clients: {},
  tasks: {},
  projects: {},
}

// Store UI con persistenza parziale
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarMobileOpen: false,
      theme: 'light',
      activeFilters: defaultFilters,

      // Toggle collasso sidebar (desktop)
      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }))
      },

      // Imposta stato sidebar esplicitamente
      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed })
      },

      // Toggle sidebar mobile
      toggleMobileSidebar: () => {
        set((state) => ({ sidebarMobileOpen: !state.sidebarMobileOpen }))
      },

      // Imposta sidebar mobile aperta/chiusa
      setMobileSidebarOpen: (open) => {
        set({ sidebarMobileOpen: open })
      },

      // Cambia tema
      setTheme: (theme) => {
        set({ theme })
      },

      // Imposta un filtro specifico per sezione
      setFilter: (section, key, value) => {
        set((state) => ({
          activeFilters: {
            ...state.activeFilters,
            [section]: {
              ...state.activeFilters[section],
              [key]: value,
            },
          },
        }))
      },

      // Cancella filtri di una sezione
      clearFilters: (section) => {
        set((state) => ({
          activeFilters: {
            ...state.activeFilters,
            [section]: {},
          },
        }))
      },

      // Cancella tutti i filtri
      clearAllFilters: () => {
        set({ activeFilters: defaultFilters })
      },
    }),
    {
      name: 'equipe-ui',
      storage: createJSONStorage(() => localStorage),
      // Persiste solo le preferenze UI rilevanti
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
)
